import { Location } from "@angular/common";
import { Component, inject, OnInit, viewChild } from "@angular/core";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { BackButtonComponent } from "@shared/components/back-button/back-button.component";
import { environment } from "../../../environments/environment";
import { ApiService } from "../../../services/api.service";
import { CompanyService } from "../../../services/company.service";
import { HealthpartnerService } from "../../../services/healthpartner.service";
import { SurveyService } from "../../../services/survey.service";
import { SurveystorageService } from "../../../services/surveystorage.service";
import { UserService } from "../../../services/user.service";
import { UserStorageService } from "../../../services/user-storage.service";
import { GroupType } from "../../enums";
import { SurveyQuestionsComponent } from "../surveyquestions/survey-questions.component";

@Component({
	selector: "app-surveygroup",
	templateUrl: "./surveygroup.component.html",
	styleUrls: ["./surveygroup.component.scss"],
	imports: [RouterLink, TranslatePipe, BackButtonComponent],
})
export class SurveygroupComponent implements OnInit {
	private router = inject(Router);
	private activeRoute = inject(ActivatedRoute);
	sanitizer = inject(DomSanitizer);

	readonly surveyQuestionComponent =
		viewChild.required<SurveyQuestionsComponent>("surveyquestions");

	public translate: TranslateService;
	public apiservice: ApiService;
	public userdataService: UserService;
	public companyservice: CompanyService;
	public healthpartnerService: HealthpartnerService;
	public surveyService: SurveyService;
	public userservice: UserStorageService;
	public surveyStorageService: SurveystorageService;
	location: Location;
	public questionGroups = [] as any;
	groupIndex: any;
	typeId: any;
	translateKey: string = "";
	public nextQuestionNumber: any;
	groupDescription: SafeHtml = "";
	enableNext: boolean = false;
	TestMode: boolean = false;
	ResponseLocked: boolean = false;
	Preparing: boolean = false;

	constructor() {
		const apiService = inject(ApiService);
		const userService = inject(UserStorageService);
		const userDataService = inject(UserService);
		const companyService = inject(CompanyService);
		const healthPartnerService = inject(HealthpartnerService);
		const surveyService = inject(SurveyService);
		const translate = inject(TranslateService);
		const location = inject(Location);
		const surveystorageService = inject(SurveystorageService);

		this.apiservice = apiService;
		this.userdataService = userDataService;
		this.companyservice = companyService;
		this.healthpartnerService = healthPartnerService;
		this.surveyService = surveyService;
		this.userservice = userService;
		this.translate = translate;
		this.location = location;
		this.surveyStorageService = surveystorageService;
	}

	ngOnInit(): void {
		const lang = this.userservice.getLanguage();
		this.translate.use(lang.shortcode);

		this.TestMode = environment.TestMode;

		if (this.surveyService.getSurveyId() != "") {
			this.apiservice
				.IsSurveyLocked(this.surveyService.getSurveyId())
				.then((locked: any) => {
					if (locked) {
						this.surveyService.setSurveyResponseLocked(locked);
						this.ResponseLocked = true;
					} else {
						this.surveyService.setSurveyResponseLocked(false);
						this.ResponseLocked = false;
					}
				})
				.catch(() => {
					// Handle error silently
				});
		}

		this.activeRoute.paramMap.subscribe((params) => {
			this.groupIndex = params.get("groupId");
			this.typeId = params.get("typeId");
		});

		if (
			this.typeId == GroupType.APV &&
			!this.surveyService.getSelectedOnlyApv()
		) {
			this.translateKey = "MiddelPage_APV";
			this.groupDescription = this.translate.instant(this.translateKey);
		} else if (
			this.typeId == GroupType.APV &&
			this.surveyService.getSelectedOnlyApv()
		) {
			const questionGroups =
				this.surveyStorageService.getQuestionGroupsSortedByNumber();
			this.groupDescription = this.sanitizer.bypassSecurityTrustHtml(
				questionGroups[this.groupIndex].Description.replace(
					/(\r\n|\n\r|\r|\n)/g,
					"<br>",
				),
			);

			const nextNumber =
				questionGroups[this.groupIndex].QuestionItems[0].Number;
			this.nextQuestionNumber =
				this.surveyStorageService.getQuestionItemByNumber(
					nextNumber,
				).QuestionNumber;

			if (this.nextQuestionNumber && this.ResponseLocked == false) {
				this.router.navigate(["survey/" + this.nextQuestionNumber]);
			}
		}

		if (this.groupIndex == 11) {
			this.groupIndex = 5;
		}

		this.questionGroups =
			this.surveyStorageService.getQuestionGroupsSortedByNumber();

		if (this.questionGroups[this.groupIndex].Description != "") {
			this.groupDescription = this.sanitizer.bypassSecurityTrustHtml(
				this.questionGroups[this.groupIndex].Description.replace(
					/(\r\n|\n\r|\r|\n)/g,
					"<br>",
				),
			);
		}

		const nextNumber =
			this.questionGroups[this.groupIndex].QuestionItems[0].Number;
		this.nextQuestionNumber =
			this.surveyStorageService.getQuestionItemByNumber(
				nextNumber,
			).QuestionNumber;

		if (
			this.surveyService.getEquipmentCalibrated() !== undefined &&
			this.ResponseLocked == false
		) {
			this.router.navigate(["survey/" + this.nextQuestionNumber]);
		}
		this.checkEnableNext();
	}

	checkEnableNext = () => {
		const groupType = this.questionGroups[this.groupIndex].GroupType;

		if (
			groupType == GroupType.HealthCheck &&
			!this.surveyService.getHealthCheckButtonEnabled()
		) {
			this.surveyService.setHasReachedHealthCheck(true);
		} else if (
			groupType == GroupType.HealthCheck &&
			this.surveyService.getHealthCheckButtonEnabled()
		) {
			this.enableNext = true;
		} else if (groupType == GroupType.APV) {
			this.enableNext = true;
		}
	};

	setCalibratedStatus = () => {
		let equipmentCal = null;

		const checkedRadio = document.querySelector(
			'input[name="calibratedEquipment"]:checked',
		) as HTMLInputElement;
		if (checkedRadio) {
			equipmentCal = checkedRadio.value;
		}

		this.surveyService.setEquipmentCalibrated(equipmentCal);
	};

	lockSurveyUserReponses = () => {
		this.Preparing = true;
		this.apiservice
			.LockUserResponse(this.surveyStorageService.getSurveyId())
			.then(() => {
				this.ResponseLocked = true;
				this.Preparing = false;
			});
	};

	back = () => {
		const groupType = this.questionGroups[this.groupIndex].GroupType;
		if (
			groupType == GroupType.HealthCheck &&
			this.surveyService.getPractitionerId() == ""
		) {
			window.history.go(-2);
		} else {
			window.history.back();
		}
	};

	next = () => {
		if (this.nextQuestionNumber !== undefined) {
			this.router.routeReuseStrategy.shouldReuseRoute = () => false;
			this.router.onSameUrlNavigation = "reload";
			this.router.navigate(["survey/" + this.nextQuestionNumber]);
		}
	};
}
